// Generic master/detail editor used by every entity catalogue screen
// (items, mobs, npcs, quests, spells). Owns the shell — filter input,
// new-entry button, list rendering, dirty/new flag bookkeeping,
// publish lifecycle — so each screen only has to provide its own
// blank-row factory, optional sanitize step, and the per-entry form.
//
// Not used by:
//  - affixes-screen (flat pool, no master/detail)
//  - config-screen, zones-screen, users-screen, access-screen
//    (custom shapes)
//
// Convention: `T` must carry a unique string `id`. New rows get a
// `__isNew` flag (CSS class `created`), edits to existing rows flip
// `__isDirty` (CSS class `dirty`). Both flags are stripped from the
// `getDraft()` output.

import { el } from "./dom.js";

interface DraftFlags {
  __isNew?: boolean;
  __isDirty?: boolean;
}

type Draft<T> = T & DraftFlags;

export interface CatalogueEditorOptions<T extends { id: string }> {
  // Singular entity name used in UI text — "+ New {entityName}" and
  // the empty-state hint "Select a {entityName} or create one".
  entityName: string;
  // Display label for the list row (defaults to `t.id`).
  getName?: (t: T) => string;
  // Factory for a brand-new entry. Returned object should already have
  // a usable default `id`; the editor will rename it on collision.
  blank: () => T;
  // Optional cleanup before publish (strip empties, clamp ranges, etc.).
  // Defaults to identity.
  sanitize?: (draft: T) => T;
  // Render the per-entry form. Called every time the selection changes
  // or `rerender()` is invoked. Mutate `draft` directly, then call
  // `markDirty()` to flag the row + propagate dirty state to the
  // parent. Call `rerender()` if a field change should rebuild the
  // editor body (e.g. switching category reveals different inputs).
  renderEditor: (args: {
    draft: T;
    markDirty: () => void;
    rerender: () => void;
  }) => HTMLElement;
  // Optional search/filter input in the list header. Defaults to true.
  // npcs / quests / spells don't have one today; pass false to opt out.
  showFilter?: boolean;
}

export interface CatalogueEditorCallbacks {
  onDirtyChange: (dirty: boolean) => void;
}

export interface CatalogueEditor<T extends { id: string }> {
  readonly root: HTMLElement;
  set(entries: readonly T[]): void;
  // Snapshot of the current draft, with sanitize applied + internal
  // __flags stripped. Pure read — does NOT mutate state.
  getDraft(): T[];
  isDirty(): boolean;
  // Call after a successful publish — clears __isNew / __isDirty on
  // every row and resets the dirty-callback to false.
  markPublished(): void;
}

export function createCatalogueEditor<T extends { id: string }>(
  opts: CatalogueEditorOptions<T>,
  cb: CatalogueEditorCallbacks,
): CatalogueEditor<T> {
  const getName = opts.getName ?? ((t: T) => t.id);
  const sanitize = opts.sanitize ?? ((t: T) => t);
  const showFilter = opts.showFilter !== false;

  let drafts: Draft<T>[] = [];
  let selectedId: string | null = null;
  let filterText = "";

  const listRows = el("div", { class: "items-list-rows" });
  const editorBody = el("div", { class: "editor" });

  const headerControls: HTMLElement[] = [];
  if (showFilter) {
    headerControls.push(
      el("input", {
        type: "search",
        placeholder: "Filter…",
        oninput: (e: Event) => {
          filterText = (e.target as HTMLInputElement).value.toLowerCase();
          renderList();
        },
      }),
    );
  }
  headerControls.push(
    el(
      "button",
      {
        class: "button secondary",
        onclick: () => {
          const fresh = opts.blank() as Draft<T>;
          // Ensure the new id doesn't collide with an existing row.
          let suffix = drafts.length;
          let baseId = fresh.id;
          while (drafts.some((d) => d.id === baseId)) {
            suffix += 1;
            baseId = `${fresh.id}_${suffix}`;
          }
          fresh.id = baseId;
          fresh.__isNew = true;
          fresh.__isDirty = true;
          drafts.push(fresh);
          selectedId = fresh.id;
          cb.onDirtyChange(true);
          renderList();
          renderEditor();
        },
      },
      `+ New ${opts.entityName}`,
    ),
  );

  const list = el(
    "div",
    { class: "items-list" },
    el("div", { class: "items-list-header" }, ...headerControls),
    listRows,
  );

  const root = el("div", { class: "items-layout" }, list, editorBody);

  function selectRow(id: string): void {
    selectedId = id;
    renderList();
    renderEditor();
  }

  function markDirty(d: Draft<T>): void {
    if (!d.__isNew) d.__isDirty = true;
    cb.onDirtyChange(true);
    renderList();
  }

  function renderList(): void {
    listRows.replaceChildren();
    const filtered = drafts
      .filter((d) => {
        if (!filterText) return true;
        const name = getName(d).toLowerCase();
        return name.includes(filterText) || d.id.toLowerCase().includes(filterText);
      })
      .sort((a, b) => getName(a).localeCompare(getName(b)));
    for (const d of filtered) {
      const row = el(
        "div",
        {
          class:
            "items-list-row" +
            (d.id === selectedId ? " active" : "") +
            (d.__isNew ? " created" : d.__isDirty ? " dirty" : ""),
          onclick: () => selectRow(d.id),
        },
        el("span", { class: "name" }, getName(d) || "(unnamed)"),
        el("span", { class: "id" }, d.id),
      );
      listRows.append(row);
    }
  }

  function renderEditor(): void {
    editorBody.replaceChildren();
    const found = drafts.find((d) => d.id === selectedId);
    if (!found) {
      editorBody.append(
        el(
          "div",
          { class: "empty" },
          `Select a ${opts.entityName} or create a new one.`,
        ),
      );
      return;
    }
    const draft = found;
    const form = opts.renderEditor({
      draft,
      markDirty: () => markDirty(draft),
      rerender: renderEditor,
    });
    editorBody.append(form);
  }

  return {
    root,
    set(entries) {
      drafts = entries.map((e) => ({ ...e })) as Draft<T>[];
      // Keep the current selection if its id still exists; otherwise
      // clear it so the empty state renders.
      if (selectedId && !drafts.some((d) => d.id === selectedId)) {
        selectedId = null;
      }
      cb.onDirtyChange(false);
      renderList();
      renderEditor();
    },
    getDraft() {
      return drafts.map((d) => {
        const { __isNew: _new, __isDirty: _dirty, ...clean } = d;
        return sanitize(clean as T);
      });
    },
    isDirty() {
      return drafts.some((d) => d.__isNew || d.__isDirty);
    },
    markPublished() {
      for (const d of drafts) {
        delete d.__isNew;
        delete d.__isDirty;
      }
      cb.onDirtyChange(false);
      renderList();
    },
  };
}
